import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ClipboardCheck, Clock, Trophy, ArrowRight, Target, TrendingUp, TrendingDown, Minus, Sparkles, Calendar, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { PageContainer } from '../../components/shared/PageContainer';
import { PageHero, HeroStatPill } from '../../components/shared/PageHero';
import { itemVariants } from '../../components/shared/animations';
import { Skeleton } from '../../components/shared/Skeleton';
import { DjayAvatar } from '../../mascot';
import { useDjaySelection } from '../../mascot';
import { GroupId } from '../../config/systemConfig';
import { error as logError } from '../../utils/logger';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { PRIMARY } from '../../config/designTokens';
import { AssessmentConfig, AssessmentDomainId, AssessmentHistoryEntry, AssessmentReport, SubTestDefinition, TeacherAssignment } from '../types';
import type { CooldownResult } from '../types';
import { getAssessmentHistoryForLearner, checkDomainCooldownForLearner, getReport } from '../utils/assessmentStorage';
import { getAvailableSubTests, DOMAIN_META, DOMAIN_MODULE_MAP } from '../utils/assessmentDomainConfig';
import { dataLayer } from '../../data/dataLayer';
import { useStore } from '../../app/store';
import { getLearnerExistingClassrooms } from '../../accounts';
const DEFAULT_QUESTION_COUNT = 30;
const URGENT_DEADLINE_MS = 2 * 24 * 60 * 60 * 1000;
interface AssessmentPageProps {
  selectedGroup: GroupId;
  onStart: (config: AssessmentConfig) => void;
  onViewReport: (report: AssessmentReport) => void;
  /**
   * AS-2026-07-P1-01: identifies WHOSE assessments this page shows. Without
   * it we cannot attribute history or cooldowns, so the page renders an
   * explicit "no learner selected" state rather than falling back to
   * group-wide data (which would leak classmates' results).
   */
  learnerId?: string;
}
export function AssessmentPage({
  selectedGroup,
  onStart,
  onViewReport,
  learnerId
}: AssessmentPageProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const [history, setHistory] = useState<AssessmentHistoryEntry[]>([]);
  const [reports, setReports] = useState<Record<string, AssessmentReport>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showAllResults, setShowAllResults] = useState(false);
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [domainCooldowns, setDomainCooldowns] = useState<Record<AssessmentDomainId, CooldownResult>>({
    rekenen: {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    },
    taalverzorging: {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    },
    'begrijpend-lezen': {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    }
  });
  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    let isMounted = true;
    // AS-2026-07-P1-01: never fall back to group-wide reads. Without a
    // learner we cannot know whose results these are, so we show nothing.
    if (!learnerId) {
      setHistory([]);
      setReports({});
      setAssignments([]);
      setLoadError(false);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }
    // Capture as a local const so the scope is unambiguous inside the async
    // closures below (and so TS keeps the non-nullable narrowing).
    const activeLearnerId: string = learnerId;
    const loadData = async () => {
      setIsLoading(true);
      setLoadError(false);
      let failed = false;
      try {
        const h = await getAssessmentHistoryForLearner(activeLearnerId, selectedGroup);
        if (isMounted) setHistory(h);
        const loadedReports: Record<string, AssessmentReport> = {};
        await Promise.all(h.map(async entry => {
          const report = await getReport(entry.attemptId);
          if (report) loadedReports[entry.attemptId] = report;
        }));
        if (isMounted) setReports(loadedReports);
        const assignmentsData = await dataLayer.getTeacherAssignments();
        const allAssignments = Object.values(assignmentsData);
        /*
         `wholeClass` werd hier alleen op groepsniveau gefilterd, niet op klas.
         Twee klassen van dezelfde leerkracht op hetzelfde groepsniveau — een
         parallelgroep 6a en 6b — leverden elkaars toetsen bij de kinderen af.
         Zie de uitgebreide toelichting in `useActiveAssignments.ts`; dit is de
         tweede plek waar dezelfde beslissing werd genomen, en dat is precies
         waarom hij hier ook fout stond.
         */
        const accountsState = useStore.getState().multiAccountState?.accounts;
        const learnerClassroomIds = accountsState ? getLearnerExistingClassrooms(accountsState, activeLearnerId).map(c => c.classroom.id) : [];
        const myAssignments = allAssignments.filter(a => {
          if (a.status !== 'active') return false;
          if (a.deadline && Date.now() > a.deadline) return false;
          const completedBy = a.completedByStudentIds || [];
          if (completedBy.includes(activeLearnerId)) return false;
          if (a.targetStudentIds && a.targetStudentIds.includes(activeLearnerId)) return true;
          if (a.wholeClass && a.group === selectedGroup) {
            const classroomId = a.classroomId ?? null;
            return classroomId === null || learnerClassroomIds.includes(classroomId);
          }
          return false;
        });
        if (isMounted) setAssignments(myAssignments.sort((a, b) => b.createdAt - a.createdAt));
      } catch (e) {
        failed = true;
        logError('Failed to load assessment history', e);
      }
      try {
        const domains: AssessmentDomainId[] = ['rekenen', 'taalverzorging', 'begrijpend-lezen'];
        const cooldowns: Record<string, CooldownResult> = {};
        await Promise.all(domains.map(async domainId => {
          cooldowns[domainId] = await checkDomainCooldownForLearner(activeLearnerId, selectedGroup, domainId);
        }));
        if (isMounted) setDomainCooldowns(cooldowns as Record<AssessmentDomainId, CooldownResult>);
      } catch (e) {
        failed = true;
        logError('Failed to check cooldowns', e);
      }
      if (isMounted) {
        setLoadError(failed);
        setIsLoading(false);
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
  }, [selectedGroup, learnerId]);
  const handleStartSubTest = (subTest: SubTestDefinition) => {
    onStart({
      questionCount: subTest.questionCount,
      moduleFilter: subTest.modules,
      minModules: 1,
      countsForMastery: false,
      seed: Date.now(),
      domainId: subTest.domainId
    });
  };
  const handleStartAssignment = (assignment: TeacherAssignment) => {
    const modules = DOMAIN_MODULE_MAP[assignment.domainId as keyof typeof DOMAIN_MODULE_MAP] || null;
    onStart({
      domainId: assignment.domainId,
      mode: assignment.type === 'targeted' ? 'targeted' : 'general',
      targetFamilyIds: assignment.selectedFamilyIds || [],
      assignmentId: assignment.id,
      questionCount: assignment.questionCount || DEFAULT_QUESTION_COUNT,
      moduleFilter: modules,
      minModules: 1,
      countsForMastery: false,
      seed: Date.now()
    });
  };
  const hasHistory = history.length > 0;
  const bestScore = hasHistory ? Math.max(...history.map(h => h.score)) : null;
  const availableSubTests = getAvailableSubTests(selectedGroup);
  const displayedHistory = showAllResults ? history : history.slice(0, 5);
  const anyDomainOnCooldown = Object.values(domainCooldowns).some(c => !c.canStart);
  const maxRemainingMs = Math.max(...Object.values(domainCooldowns).map(c => c.remainingMs));
  return <PageContainer className="space-y-5 sm:space-y-6 pb-20" data-id="element-766" data-mp="2ppbq490vir6f">
      {/* Hero */}
      <motion.div variants={itemVariants} data-id="element-767" data-mp="2552x223vzmcr">
        <PageHero title="Toetsen" subtitle={hasHistory ? 'Laat zien hoeveel je gegroeid bent!' : 'Ontdek waar je staat met een toets!'} gradientClasses={PRIMARY.gradient} djayEmotion={hasHistory ? 'excited' : 'encouraging'} djayVariant={selectedVariant} icon={ClipboardCheck} iconBgClass="bg-orange-400" floatingEmojis={['📝', '⭐']} data-id="element-768" data-mp="256e1m455oy4x">
          
          <HeroStatPill icon={Target} data-id="element-769" data-mp="2o3oyxj789hhq">Groep {selectedGroup}</HeroStatPill>
          {hasHistory && <HeroStatPill icon={ClipboardCheck} data-id="element-770" data-mp="2s3z26o3mxtsw">
              {history.length} toets{history.length !== 1 ? 'en' : ''} gemaakt
            </HeroStatPill>}
          {bestScore !== null && <HeroStatPill icon={Trophy} data-id="element-771" data-mp="31uajdccbxzhq">Topscore: {bestScore}%</HeroStatPill>}
        </PageHero>
      </motion.div>

      {/* Teacher-Assigned Assessments */}
      {assignments.length > 0 && <motion.div variants={itemVariants} data-id="element-772" data-mp="36rait4eoc3zv">
          <Card className="p-6 sm:p-8" data-id="element-773" data-mp="3brormoo3nhyy">
            <SectionHeader title="Van je Leerkracht" icon={Sparkles} accentColor="bg-indigo-100 text-indigo-600" description="Je leerkracht heeft een toets voor je klaargezet. Doe rustig je best!" className="mb-6" data-id="element-774" data-mp="3bsq48xkrkcis" />
          
            <div className="space-y-3" data-id="element-775" data-mp="q6yrcimyehjh">
              {assignments.map(assignment => {
            const meta = DOMAIN_META[assignment.domainId as keyof typeof DOMAIN_META];
            const Icon = meta?.icon;
            const hasDeadline = assignment.deadline;
            const isUrgent = hasDeadline && assignment.deadline - Date.now() < URGENT_DEADLINE_MS;
            return <button key={assignment.id} onClick={() => handleStartAssignment(assignment)} className={`w-full text-left rounded-2xl p-4 border-2 transition-all hover:shadow-md flex items-center gap-4 ${isUrgent ? 'border-orange-300 bg-orange-50/50' : 'border-white bg-white hover:border-indigo-200'}`} data-id="element-776" data-mp="1dxb3k5p8f0fv">
                  
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${meta?.bgColor || 'bg-slate-100'} ${meta?.color || 'text-slate-500'}`} data-id="element-777" data-mp="1wvdqmey7ynb1">
                    
                      {Icon ? <Icon className="w-6 h-6" data-id="element-778" data-mp="3m8calmcyav2a" /> : <ClipboardCheck className="w-6 h-6" data-id="element-779" data-mp="3m945euyq4ori" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-780" data-mp="42ptf1k0movr">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-781" data-mp="40d586nnzia9">
                        <h3 className="font-black text-base text-slate-900" data-id="element-782" data-mp="3v763b1n5v1ak">
                          {assignment.domainLabel}
                        </h3>
                        {assignment.type === 'targeted' && <Badge variant="default" className="bg-purple-100 text-purple-700 border-purple-200 text-[10px]" data-id="element-783" data-mp="90rchmhfyo78">
                        
                            Gericht
                          </Badge>}
                        {isUrgent && <Badge variant="default" className="bg-orange-100 text-orange-700 border-orange-300 text-[10px] animate-pulse" data-id="element-784" data-mp="hvxj2nojdaat">
                        
                            Bijna deadline!
                          </Badge>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 font-medium" data-id="element-785" data-mp="1bf56nbbzm1xq">
                        <span className="flex items-center gap-1" data-id="element-786" data-mp="1fevtx52b1zxi">
                          <Clock className="w-3 h-3" data-id="element-787" data-mp="11gforik3chhh" />
                          ~15 min
                        </span>
                        {hasDeadline && <span className={`flex items-center gap-1 ${isUrgent ? 'text-orange-600 font-bold' : ''}`} data-id="element-788" data-mp="16d66097mtj4h">
                        
                            <Calendar className="w-3 h-3" data-id="element-789" data-mp="wgb6po3jbep5" />
                            {new Date(assignment.deadline).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'short'
                    })}
                          </span>}
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-indigo-400 flex-shrink-0" data-id="element-790" data-mp="8s0cv4w9afad" />
                  </button>;
          })}
            </div>
          </Card>
        </motion.div>}

      {/* Niveaubepaling */}
      <motion.div variants={itemVariants} data-id="element-791" data-mp="3qrw4ndhobx4y">
        <Card className="p-6 sm:p-8" data-id="element-792" data-mp="3qsnu06kogyms">
          <SectionHeader title="Ontdek je Niveau" icon={Target} accentColor="bg-orange-100 text-orange-600" description="Kies een toets om te laten zien wat je al kan! Geen stress, gewoon je best doen." className="mb-6" data-id="element-793" data-mp="3lzum59twxa6h" />
          
          {isLoading ? <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-794" data-mp="3bzu0vichgjzn">
              {[1, 2, 3].map(i => <div key={i} className="bg-white/50 rounded-2xl p-4 border-2 border-white" data-id="element-795" data-mp="djjegcrs0aod">
              
                  <Skeleton className="w-10 h-10 rounded-xl mb-3" data-id="element-796" data-mp="8j542o2pcon6" />
                  <Skeleton className="h-5 w-3/4 mb-2" data-id="element-797" data-mp="3l3i9vdw6b80" />
                  <Skeleton className="h-4 w-full mb-3" data-id="element-798" data-mp="3usg44nppzcu9" />
                  <Skeleton className="h-3 w-20" data-id="element-799" data-mp="3urwj2y4rw8c0" />
                </div>)}
            </div> : <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-800" data-mp="2odf3w5tnw8hv">
              {availableSubTests.map((subTest, i) => {
            const meta = DOMAIN_META[subTest.domainId];
            const cooldown = domainCooldowns[subTest.domainId];
            const canStartThis = cooldown?.canStart !== false && !isLoading;
            return <motion.div key={subTest.domainId} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.08
            }} data-id="element-801" data-mp="13ul5x5dtca6d" data-mpi={i}>
                  
                    <button onClick={() => canStartThis && handleStartSubTest(subTest)} disabled={!canStartThis} className={`w-full text-left rounded-2xl p-4 border-2 transition-all flex flex-col h-full ${!canStartThis ? 'opacity-50 cursor-not-allowed border-slate-200 bg-slate-50' : `bg-white hover:shadow-lg hover:-translate-y-0.5 ${meta.borderColor}`}`} data-id="element-802" data-mp="1iify8knwscix">
                    
                      <div className="flex items-center gap-3 mb-3" data-id="element-803" data-mp="3tc2axrtrfzoo">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${meta.bgColor} ${meta.color}`} data-id="element-804" data-mp="24y715swwtkh">
                        
                          <meta.icon className="w-5 h-5" data-id="element-805" data-mp="1skrzqu95fw1v" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-806" data-mp="1nlxb3boi3d9u">
                          <h3 className={`font-black text-base ${meta.color}`} data-id="element-807" data-mp="21mrhxpvwh66e">
                            {subTest.label}
                          </h3>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 font-medium mb-3 flex-grow leading-relaxed" data-id="element-808" data-mp="19vby23lq9xjv">
                        {meta.description}
                      </p>
                      <div className="flex items-center gap-3 text-[11px] text-slate-500 font-medium" data-id="element-809" data-mp="14uny2sfiu6di">
                        <span className="flex items-center gap-1" data-id="element-810" data-mp="1ipqfabptji5m">
                          <ClipboardCheck className="w-3 h-3" data-id="element-811" data-mp="1dvm6ro0wn22f" />
                          {subTest.questionCount}v
                        </span>
                        <span className="flex items-center gap-1" data-id="element-812" data-mp="m69888gxwyea">
                          <Clock className="w-3 h-3" data-id="element-813" data-mp="m3wte46hb45g" />~
                          {subTest.estimatedMinutes}m
                        </span>
                        {subTest.shortNote && <span className={`px-1.5 py-0.5 rounded ${meta.bgColor} ${meta.color} font-bold`} data-id="element-814" data-mp="gtud7pep2gc4">
                        
                            {subTest.shortNote}
                          </span>}
                      </div>
                      {cooldown && !cooldown.canStart && <div className="mt-2 text-[11px] font-bold px-2 py-1 rounded-md bg-slate-100 text-slate-500 inline-block" data-id="element-815" data-mp="us0sdobv5rmg">
                          ⏳ Nog {formatRemainingTime(cooldown.remainingMs)}
                        </div>}
                    </button>
                  </motion.div>;
          })}
            </div>}

          {anyDomainOnCooldown && <div className="mt-4 bg-white/60 border border-white rounded-xl p-3 flex items-center gap-3 text-sm text-slate-600 font-medium" data-id="element-816" data-mp="prwabtvb4ou4">
              <span className="text-lg" data-id="element-817" data-mp="3ajga3l7a82qw">⏳</span>
              <span data-id="element-818" data-mp="3akhpg1vbzbef">
                Je hebt recent een toets gemaakt. Nog{' '}
                <strong data-id="element-819" data-mp="1gzxtfcr3hfw6">{formatRemainingTime(maxRemainingMs)}</strong> wachten.
              </span>
            </div>}
        </Card>
      </motion.div>

      {/* Results */}
      <motion.div variants={itemVariants} data-id="element-820" data-mp="17awelpwad4js">
        <Card className="p-6 sm:p-8" data-id="element-821" data-mp="179v1lfvpm7zf">
          <SectionHeader title="Jouw Resultaten" icon={Trophy} accentColor="bg-amber-100 text-amber-600" description={hasHistory ? 'Kijk eens hoe goed je het hebt gedaan!' : 'Na je eerste toets zie je hier je resultaten!'} className="mb-6" data-id="element-822" data-mp="178tgeikzc53z" />
          
          {isLoading ? <div className="space-y-2" data-id="element-823" data-mp="t9ts5c1dps8c">
              {[1, 2, 3].map(i => <div key={i} className="bg-white/50 rounded-xl p-3 border border-white flex items-center gap-3" data-id="element-824" data-mp="obs92r3ydswv">
              
                  <Skeleton className="w-10 h-10 rounded-xl" data-id="element-825" data-mp="jeikfdoe1la3" />
                  <div className="flex-1" data-id="element-826" data-mp="2iquuaclyyotz">
                    <Skeleton className="h-4 w-32 mb-1" data-id="element-827" data-mp="2ipt759roibvw" />
                    <Skeleton className="h-3 w-48" data-id="element-828" data-mp="2dqqdelh5fs38" />
                  </div>
                </div>)}
            </div> : loadError ? (/* AS-2026-07-P3-02: a failed load must not look like "no results yet". */
        <div className="text-center py-6" role="alert" data-id="element-829" data-mp="2q9c5cn9hubp2">
              <div className="text-4xl mb-2" data-id="element-830" data-mp="2q7rb6yr3curq">😕</div>
              <p className="text-slate-800 font-bold mb-1" data-id="element-831" data-mp="2v1ltbxr1t1op">
                Resultaten konden niet geladen worden
              </p>
              <p className="text-sm text-slate-500 font-medium mb-3" data-id="element-832" data-mp="2zyvpdw8y0bli">
                Je oude toetsen zijn niet verdwenen — we konden ze even niet
                ophalen.
              </p>
              <Button onClick={() => window.location.reload()} variant="secondary" size="sm" data-id="element-833" data-mp="27bi08kbhkkby">
              
                Opnieuw proberen
              </Button>
            </div>) : !learnerId ? <div className="text-center py-6" data-id="element-834" data-mp="2h9xirywg6l9u">
              <div className="text-4xl mb-2" data-id="element-835" data-mp="3np88825q7sqv">👤</div>
              <p className="text-slate-800 font-bold mb-1" data-id="element-836" data-mp="3nogc8q87vt02">
                Geen leerling geselecteerd
              </p>
              <p className="text-sm text-slate-500 font-medium" data-id="element-837" data-mp="3smhw3ddgvwcl">
                Kies eerst een leerling om toetsresultaten te bekijken.
              </p>
            </div> : hasHistory ? <div className="space-y-2" data-id="element-838" data-mp="cp7e18l25ewc">
              {displayedHistory.map((entry, index) => {
            const date = new Date(entry.completedAt);
            const dateStr = date.toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short'
            });
            const report = reports[entry.attemptId];
            const prevEntry = history.slice(index + 1).find(h => h.domainId === entry.domainId);
            const domainMeta = entry.domainId ? DOMAIN_META[entry.domainId] : null;
            const isAssigned = !!entry.assignmentId;
            // Score color
            const scoreBg = entry.score >= 70 ? 'bg-emerald-500' : entry.score >= 50 ? 'bg-amber-500' : 'bg-rose-500';
            // Trend
            let trendEl = null;
            if (prevEntry) {
              if (entry.score > prevEntry.score) {
                trendEl = <TrendingUp className="w-3.5 h-3.5 text-emerald-500" data-id="element-839" data-mp="1mlcjvs5gm4xf" />;
              } else if (entry.score < prevEntry.score) {
                trendEl = <TrendingDown className="w-3.5 h-3.5 text-rose-400" data-id="element-840" data-mp="1rkfqvi502l81" />;
              }
            }
            return <div key={entry.attemptId} className="bg-white rounded-xl p-3 flex items-center gap-3 hover:shadow-sm transition-shadow border border-slate-100" data-id="element-841" data-mp="1cvjitl2y187f" data-mpi={index}>
                  
                    {/* Score circle */}
                    <div className={`w-11 h-11 rounded-xl ${scoreBg} flex items-center justify-center flex-shrink-0`} data-id="element-842" data-mp="13xr4ok8yk6lm">
                    
                      <span className="text-white font-black text-sm" data-id="element-843" data-mp="ty09shghjpl0">
                        {entry.score}%
                      </span>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0" data-id="element-844" data-mp="2oazw0tfdfi19">
                      <div className="flex items-center gap-1.5" data-id="element-845" data-mp="2o95bhg80hrxp">
                        <span className="text-sm font-bold text-slate-900" data-id="element-846" data-mp="2t91fhfgpljd9">
                          {domainMeta ? domainMeta.shortLabel : 'Toets'}
                        </span>
                        {domainMeta && <span className={`w-4 h-4 rounded flex items-center justify-center ${domainMeta.bgColor} ${domainMeta.color}`} data-id="element-847" data-mp="5gmq3g44bgy5">
                        
                            <domainMeta.icon className="w-2.5 h-2.5" data-id="element-848" data-mp="3ssnlqgv033nm" />
                          </span>}
                        {trendEl}
                        {isAssigned && <Badge variant="default" className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[9px] px-1 py-0" data-id="element-849" data-mp="kdvjsggw8wt1">
                        
                            Leerkracht
                          </Badge>}
                      </div>
                      <p className="text-[11px] text-slate-400 font-medium mt-0.5" data-id="element-850" data-mp="fdhj5jhmgp51">
                        {entry.totalQuestions} vragen · {dateStr}
                      </p>
                    </div>

                    {/* Action */}
                    {report && !isAssigned ? <button onClick={() => onViewReport(report)} className="text-xs font-bold text-amber-600 hover:text-amber-700 flex items-center gap-1 flex-shrink-0" data-id="element-851" data-mp="301vkh4z2votf">
                    
                        Rapport <ArrowRight className="w-3 h-3" data-id="element-852" data-mp="2nnytzxxwdxzt" />
                      </button> : isAssigned ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-853" data-mp="31km2mxz7qclk" /> : null}
                  </div>;
          })}

              {history.length > 5 && <button onClick={() => setShowAllResults(!showAllResults)} className="w-full text-center text-xs font-bold text-slate-500 hover:text-slate-700 py-2" data-id="element-854" data-mp="28vye8tj6373s">
              
                  {showAllResults ? 'Toon minder' : `Toon alle resultaten (${history.length})`}
                </button>}
            </div> : <div className="text-center py-6" data-id="element-855" data-mp="2dvku7aqdr1ga">
              <div className="text-4xl mb-2" data-id="element-856" data-mp="2dtq83t3dh3am">🌟</div>
              <p className="text-slate-800 font-bold mb-1" data-id="element-857" data-mp="1q56xh6wuoefd">
                Nog geen resultaten
              </p>
              <p className="text-sm text-slate-500 font-medium" data-id="element-858" data-mp="1l6dui8f6w5tg">
                Start hierboven een toets om te zien waar je staat.
              </p>
            </div>}
        </Card>
      </motion.div>
    </PageContainer>;
}
function formatRemainingTime(ms: number): string {
  const minutes = Math.ceil(ms / (60 * 1000));
  if (minutes <= 1) return 'minder dan een minuut';
  if (minutes < 60) return `${minutes} minuten`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours} uur`;
  return `${hours} uur en ${remainingMinutes} minuten`;
}